import React, { useState, useEffect, useCallback } from "react";
import { Container, Typography, Card, CardContent, Box } from "@mui/material";
import { getDocs, updateDoc, doc, arrayUnion, arrayRemove, collection } from "firebase/firestore";
import { db } from "../../firebase-config";
import { useAuth } from "../../context/AuthContext";
import MyTeams from "./MyTeams";
import MemberCard from "./MemberCard";
import AddMemberAutocomplete from "./AddMemberAutocomplete";

const ManageTeam = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const fetchUserTeams = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const snapshot = await getDocs(collection(db, "teams"));
      const teamList = snapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .filter((team) => team.createdBy === user.uid);
      setTeams(teamList);
    } catch (e) {
      console.error(e);
      setError("Failed to load your teams.");
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (user) fetchUserTeams();
  }, [user, fetchUserTeams]);

  const handleAcceptRequest = async (teamId, uid) => {
    try {
      const teamRef = doc(db, "teams", teamId);
      await updateDoc(teamRef, {
        pendingRequests: arrayRemove(uid),
        members: arrayUnion(uid),
      });
      setMsg("User accepted.");
      fetchUserTeams();
    } catch (e) {
      console.error(e);
      setError("Failed to accept request.");
    }
  };

  const handleRejectRequest = async (teamId, uid) => {
    try {
      const teamRef = doc(db, "teams", teamId);
      await updateDoc(teamRef, { pendingRequests: arrayRemove(uid) });
      setMsg("User rejected.");
      fetchUserTeams();
    } catch (e) {
      console.error(e);
      setError("Failed to reject request.");
    }
  };

  const handleRemoveMember = async (teamId, uid) => {
    try {
      const teamRef = doc(db, "teams", teamId);
      await updateDoc(teamRef, { members: arrayRemove(uid) });
      setMsg("User removed.");
      fetchUserTeams();
    } catch (e) {
      console.error(e);
      setError("Failed to remove member.");
    }
  };

  const handleAddMember = async (teamId, uid) => {
    try {
      const teamRef = doc(db, "teams", teamId);
      await updateDoc(teamRef, { members: arrayUnion(uid) });
      setMsg("User added.");
      fetchUserTeams();
    } catch (e) {
      console.error(e);
      setError("Failed to add new member.");
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Team Management
      </Typography>
      {error && (
        <Typography variant="body1" color="error" gutterBottom>
          {error}
        </Typography>
      )}
      {msg && (
        <Typography variant="body1" color="success.main" gutterBottom>
          {msg}
        </Typography>
      )}
      <Typography variant="h6" gutterBottom>
        Manage Your Teams (Owned)
      </Typography>
      {loading ? (
        <Typography>Loading teams...</Typography>
      ) : teams.length === 0 ? (
        <Typography>You haven’t created any teams yet.</Typography>
      ) : (
        teams.map((team) => (
          <Card key={team.id} sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6">{team.teamName}</Typography>
              <Typography variant="body2">
                Description: {team.description || "N/A"}
              </Typography>
              <Typography variant="body2">
                Pending Requests: {team.pendingRequests ? team.pendingRequests.length : 0}
              </Typography>
              {team.pendingRequests && team.pendingRequests.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2">
                    Pending Join Requests:
                  </Typography>
                  {team.pendingRequests.map((uid) => (
                    <MemberCard
                      key={uid}
                      uid={uid}
                      pending={true}
                      onAccept={() => handleAcceptRequest(team.id, uid)}
                      onReject={() => handleRejectRequest(team.id, uid)}
                    />
                  ))}
                </Box>
              )}
              {team.members && team.members.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2">Team Members:</Typography>
                  {team.members.map((uid) => (
                    <MemberCard
                      key={uid}
                      uid={uid}
                      pending={false}
                      isCreator={uid === team.createdBy}
                      onRemove={
                        uid === team.createdBy ? null : () => handleRemoveMember(team.id, uid)
                      }
                    />
                  ))}
                </Box>
              )}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Add New Member:</Typography>
                <AddMemberAutocomplete
                  existingMemberUids={team.members || []}
                  onMemberAdded={(newUid) => handleAddMember(team.id, newUid)}
                />
              </Box>
            </CardContent>
          </Card>
        ))
      )}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h6" gutterBottom>
          Teams You're a Member Of (Not Owned)
        </Typography>
        <MyTeams />
      </Box>
    </Container>
  );
};

export default ManageTeam;